import { forwardRef } from 'react';
import {
  AddFolderIcon,
  AddIcon,
  AltArrowDownIcon,
  AltArrowLeftIcon,
  AltArrowRightIcon,
  AltArrowUpIcon,
  ArchiveIcon,
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowRightUpIcon,
  ArrowUpIcon,
  BellIcon,
  BellOffIcon,
  BellRingIcon,
  BillListIcon,
  BookIcon,
  BotIcon,
  Buildings2Icon,
  CalendarDateIcon,
  CalendarIcon,
  CalendarMarkIcon,
  CaseIcon,
  CaseRoundIcon,
  ChatDotsIcon,
  ChatSquareIcon,
  CheckCircleIcon,
  CheckReadIcon,
  ChecklistIcon,
  ClipboardCheckIcon,
  ClipboardListIcon,
  ClockCircleIcon,
  CloseCircleIcon,
  CloseIcon,
  CodeFileIcon,
  CodeIcon,
  CopyIcon,
  CpuIcon,
  CupHotIcon,
  CupStarIcon,
  CursorSquareIcon,
  DangerCircleIcon,
  DangerTriangleIcon,
  DisketteIcon,
  DollarIcon,
  DownloadIcon,
  EyeClosedIcon,
  EyeIcon,
  FileCheckIcon,
  FileCorruptedIcon,
  FileFavoriteIcon,
  FileIcon,
  FileTextIcon,
  FilterIcon,
  FolderIcon,
  FolderOpenIcon,
  FolderWithFilesIcon,
  GalleryAddIcon,
  GalleryIcon,
  GlobalIcon,
  GraphUpIcon,
  HamburgerMenuIcon,
  HandHeartIcon,
  HeadphonesRoundIcon,
  HeartIcon,
  HeartPulseIcon,
  HistoryIcon,
  HouseIcon,
  InboxIcon,
  InfoCircleIcon,
  KeyIcon,
  KeyMinimalisticIcon,
  LayersIcon,
  LetterIcon,
  LinkIcon,
  LinkMinimalisticIcon,
  LinkRoundAngleIcon,
  ListCheckIcon,
  ListIcon,
  ListVerticalIcon,
  LockIcon,
  LockKeyholeIcon,
  LogoutIcon,
  MagicWand2Icon,
  MagnifierIcon,
  MapPointIcon,
  MaximizeIcon,
  MenuDotsIcon,
  MinimizeIcon,
  MinusIcon,
  PaletteIcon,
  PaperclipIcon,
  PauseIcon,
  PenIcon,
  PenNewSquareIcon,
  PhoneCallingIcon,
  PhoneIcon,
  PlayCircleIcon,
  PlayIcon,
  PulseIcon,
  QuestionCircleIcon,
  RecordCircleIcon,
  RefreshCircleIcon,
  RefreshIcon,
  RepeatIcon,
  SendSquareIcon,
  SettingsIcon,
  ShareIcon,
  ShieldCheckIcon,
  ShieldIcon,
  ShieldWarningIcon,
  SidebarIcon,
  SirenIcon,
  SliderHorizontalIcon,
  SmileCircleIcon,
  SquareAcademicCapIcon,
  StarIcon,
  StarsIcon,
  StethoscopeIcon,
  TagIcon,
  TargetIcon,
  TextCircleIcon,
  TicketIcon,
  TramIcon,
  TrashBin2Icon,
  UndoLeftIcon,
  UndoRightIcon,
  UploadIcon,
  UserCheckIcon,
  UserIcon,
  UserPlusIcon,
  UserRoundedIcon,
  UsersGroupRoundedIcon,
  VerifiedCheckIcon,
  VideocameraIcon,
  WalletIcon,
  WiFiIcon,
  Widget2Icon,
  Widget5Icon,
  WidgetIcon,
} from '@solar-icons/react/bold-duotone';

// Compatibility layer: existing screens keep their Lucide-shaped imports while
// rendering the locally installed Solar Bold Duotone icon set.

/**
 * Props accepted by the compatibility icons: every SVG attribute plus the
 * Lucide-shaped `size`/`title` and the Solar duotone color controls.
 *
 * @typedef {import('react').SVGProps<SVGSVGElement> & {
 *   size?: number | string,
 *   title?: string,
 *   secondaryColor?: string,
 *   secondaryOpacity?: number,
 * }} SolarIconProps
 */

/**
 * @param {any} Icon
 * @returns {import('react').ForwardRefExoticComponent<SolarIconProps & import('react').RefAttributes<SVGSVGElement>>}
 */
function solarIcon(Icon) {
  return forwardRef(
    /**
     * @param {SolarIconProps} props
     * @param {import('react').ForwardedRef<SVGSVGElement>} ref
     */
    function SolarIcon({ secondaryColor, secondaryOpacity, ...props }, ref) {
      return (
        <Icon
          {...props}
          ref={ref}
          secondaryColor={secondaryColor ?? 'currentColor'}
          secondaryOpacity={secondaryOpacity ?? 0.34}
        />
      );
    },
  );
}

export const Activity = solarIcon(PulseIcon);
export const AlertTriangle = solarIcon(DangerTriangleIcon);
export const Archive = solarIcon(ArchiveIcon);
export const ArrowDown = solarIcon(ArrowDownIcon);
export const ArrowDownToLine = solarIcon(DownloadIcon);
export const ArrowLeft = solarIcon(ArrowLeftIcon);
export const ArrowRight = solarIcon(ArrowRightIcon);
export const ArrowUp = solarIcon(ArrowUpIcon);
export const ArrowUpRight = solarIcon(ArrowRightUpIcon);
export const Award = solarIcon(CupStarIcon);
export const BadgeCheck = solarIcon(VerifiedCheckIcon);
export const BarChart3 = solarIcon(GraphUpIcon);
export const Bell = solarIcon(BellIcon);
export const BellOff = solarIcon(BellOffIcon);
export const BellRing = solarIcon(BellRingIcon);
export const Blocks = solarIcon(WidgetIcon);
export const BookOpen = solarIcon(BookIcon);
export const Bot = solarIcon(BotIcon);
export const Braces = solarIcon(CodeIcon);
export const Briefcase = solarIcon(CaseIcon);
export const BriefcaseBusiness = solarIcon(CaseRoundIcon);
export const Building2 = solarIcon(Buildings2Icon);
export const CakeSlice = solarIcon(CupHotIcon);
export const Calendar = solarIcon(CalendarIcon);
export const CalendarCheck = solarIcon(CalendarMarkIcon);
export const CalendarCheck2 = solarIcon(CalendarMarkIcon);
export const CalendarClock = solarIcon(CalendarDateIcon);
export const CalendarDays = solarIcon(CalendarDateIcon);
export const Check = solarIcon(CheckReadIcon);
export const CheckCheck = solarIcon(CheckReadIcon);
export const CheckCircle2 = solarIcon(CheckCircleIcon);
export const ChevronDown = solarIcon(AltArrowDownIcon);
export const ChevronLeft = solarIcon(AltArrowLeftIcon);
export const ChevronRight = solarIcon(AltArrowRightIcon);
export const ChevronUp = solarIcon(AltArrowUpIcon);
export const Circle = solarIcon(RecordCircleIcon);
export const CircleAlert = solarIcon(DangerCircleIcon);
export const CircleCheck = solarIcon(CheckCircleIcon);
export const CircleDollarSign = solarIcon(DollarIcon);
export const CircleHelp = solarIcon(QuestionCircleIcon);
export const ClipboardCheck = solarIcon(ClipboardCheckIcon);
export const ClipboardList = solarIcon(ClipboardListIcon);
export const Clock = solarIcon(ClockCircleIcon);
export const Clock3 = solarIcon(ClockCircleIcon);
export const Coffee = solarIcon(CupHotIcon);
export const Columns3 = solarIcon(Widget2Icon);
export const Copy = solarIcon(CopyIcon);
export const Cpu = solarIcon(CpuIcon);
export const Download = solarIcon(DownloadIcon);
export const Edit3 = solarIcon(PenNewSquareIcon);
export const ExternalLink = solarIcon(LinkRoundAngleIcon);
export const Eye = solarIcon(EyeIcon);
export const EyeOff = solarIcon(EyeClosedIcon);
export const Facebook = solarIcon(LinkIcon);
export const File = solarIcon(FileIcon);
export const FileBadge = solarIcon(FileFavoriteIcon);
export const FileCheck2 = solarIcon(FileCheckIcon);
export const FileCode2 = solarIcon(CodeFileIcon);
export const FileHeart = solarIcon(FileFavoriteIcon);
export const FileKey2 = solarIcon(KeyIcon);
export const FileSearch = solarIcon(FileTextIcon);
export const FileSignature = solarIcon(PenNewSquareIcon);
export const FileSpreadsheet = solarIcon(FileTextIcon);
export const FileText = solarIcon(FileTextIcon);
export const FileWarning = solarIcon(FileCorruptedIcon);
export const Filter = solarIcon(FilterIcon);
export const Folder = solarIcon(FolderIcon);
export const FolderKanban = solarIcon(FolderWithFilesIcon);
export const FolderOpen = solarIcon(FolderOpenIcon);
export const FolderPlus = solarIcon(AddFolderIcon);
export const Globe2 = solarIcon(GlobalIcon);
export const GraduationCap = solarIcon(SquareAcademicCapIcon);
export const GripVertical = solarIcon(ListVerticalIcon);
export const Headset = solarIcon(HeadphonesRoundIcon);
export const Heart = solarIcon(HeartIcon);
export const HeartHandshake = solarIcon(HandHeartIcon);
export const HeartPulse = solarIcon(HeartPulseIcon);
export const HelpCircle = solarIcon(QuestionCircleIcon);
export const History = solarIcon(HistoryIcon);
export const House = solarIcon(HouseIcon);
export const Image = solarIcon(GalleryIcon);
export const ImagePlus = solarIcon(GalleryAddIcon);
export const Images = solarIcon(GalleryIcon);
export const Inbox = solarIcon(InboxIcon);
export const Info = solarIcon(InfoCircleIcon);
export const Instagram = solarIcon(LinkIcon);
export const KeyRound = solarIcon(KeyMinimalisticIcon);
export const Layers3 = solarIcon(LayersIcon);
export const LayoutDashboard = solarIcon(Widget5Icon);
export const LayoutGrid = solarIcon(WidgetIcon);
export const LayoutList = solarIcon(ListIcon);
export const LayoutPanelTop = solarIcon(SidebarIcon);
export const Link2 = solarIcon(LinkMinimalisticIcon);
export const Linkedin = solarIcon(LinkIcon);
export const ListChecks = solarIcon(ListCheckIcon);
export const ListTodo = solarIcon(ChecklistIcon);
export const Loader2 = solarIcon(RefreshIcon);
export const LoaderCircle = solarIcon(RefreshCircleIcon);
export const Lock = solarIcon(LockIcon);
export const LockKeyhole = solarIcon(LockKeyholeIcon);
export const LogOut = solarIcon(LogoutIcon);
export const Mail = solarIcon(LetterIcon);
export const MapPin = solarIcon(MapPointIcon);
export const Maximize2 = solarIcon(MaximizeIcon);
export const Megaphone = solarIcon(SirenIcon);
export const Menu = solarIcon(HamburgerMenuIcon);
export const MessageCircle = solarIcon(ChatDotsIcon);
export const MessageSquare = solarIcon(ChatSquareIcon);
export const MessageSquarePlus = solarIcon(ChatSquareIcon);
export const MessageSquareQuote = solarIcon(ChatSquareIcon);
export const Minimize2 = solarIcon(MinimizeIcon);
export const Minus = solarIcon(MinusIcon);
export const MoreHorizontal = solarIcon(MenuDotsIcon);
export const Newspaper = solarIcon(FileTextIcon);
export const Palette = solarIcon(PaletteIcon);
export const PanelLeft = solarIcon(SidebarIcon);
export const Paperclip = solarIcon(PaperclipIcon);
export const PenLine = solarIcon(PenIcon);
export const Pencil = solarIcon(PenIcon);
export const Pause = solarIcon(PauseIcon);
export const Phone = solarIcon(PhoneIcon);
export const PhoneCall = solarIcon(PhoneCallingIcon);
export const Play = solarIcon(PlayIcon);
export const PlayCircle = solarIcon(PlayCircleIcon);
export const Plus = solarIcon(AddIcon);
export const Quote = solarIcon(TextCircleIcon);
export const ReceiptText = solarIcon(BillListIcon);
export const Redo2 = solarIcon(UndoRightIcon);
export const RefreshCw = solarIcon(RefreshIcon);
export const Repeat2 = solarIcon(RepeatIcon);
export const Sandwich = solarIcon(HamburgerMenuIcon);
export const Save = solarIcon(DisketteIcon);
export const Search = solarIcon(MagnifierIcon);
export const Send = solarIcon(SendSquareIcon);
export const Settings = solarIcon(SettingsIcon);
export const Settings2 = solarIcon(SettingsIcon);
export const Share = solarIcon(ShareIcon);
export const Share2 = solarIcon(ShareIcon);
export const Shield = solarIcon(ShieldIcon);
export const ShieldAlert = solarIcon(ShieldWarningIcon);
export const ShieldCheck = solarIcon(ShieldCheckIcon);
export const SlidersHorizontal = solarIcon(SliderHorizontalIcon);
export const SmilePlus = solarIcon(SmileCircleIcon);
export const Sparkles = solarIcon(StarsIcon);
export const SpellCheck = solarIcon(ListCheckIcon);
export const SquareMousePointer = solarIcon(CursorSquareIcon);
export const Star = solarIcon(StarIcon);
export const Stethoscope = solarIcon(StethoscopeIcon);
export const Table2 = solarIcon(Widget2Icon);
export const Tag = solarIcon(TagIcon);
export const Target = solarIcon(TargetIcon);
export const Ticket = solarIcon(TicketIcon);
export const ToggleLeft = solarIcon(MinusIcon);
export const ToggleRight = solarIcon(AddIcon);
export const TrainFront = solarIcon(TramIcon);
export const Trash2 = solarIcon(TrashBin2Icon);
export const TrendingUp = solarIcon(GraphUpIcon);
export const Trophy = solarIcon(CupStarIcon);
export const Undo2 = solarIcon(UndoLeftIcon);
export const Upload = solarIcon(UploadIcon);
export const User = solarIcon(UserIcon);
export const UserCheck = solarIcon(UserCheckIcon);
export const UserPlus = solarIcon(UserPlusIcon);
export const UserRound = solarIcon(UserRoundedIcon);
export const UserRoundCheck = solarIcon(UserCheckIcon);
export const Users = solarIcon(UsersGroupRoundedIcon);
export const UsersRound = solarIcon(UsersGroupRoundedIcon);
export const Video = solarIcon(VideocameraIcon);
export const WalletCards = solarIcon(WalletIcon);
export const Wand2 = solarIcon(MagicWand2Icon);
export const Wifi = solarIcon(WiFiIcon);
export const X = solarIcon(CloseIcon);
export const XCircle = solarIcon(CloseCircleIcon);
